declare module '@walkeros/core' {
  interface SourceMap {
    cli: { type: 'cli'; platform: 'terminal'; command?: string };
  }
}

// === CLI Commands ===
// Export CLI command handlers
export { bundleCommand } from './commands/bundle/index.js';
export { pushCommand } from './commands/push/index.js';
export { runCommand } from './commands/run/index.js';
export { validateCommand } from './commands/validate/index.js';
export {
  loginCommand,
  login,
  completeDeviceLogin,
} from './commands/login/index.js';
export type {
  LoginOptions,
  LoginResult,
  CompleteDeviceLoginOptions,
  DeviceLoginResult,
} from './commands/login/index.js';
export { logoutCommand, logout } from './commands/logout/index.js';
export { whoamiCommand } from './commands/auth/index.js';
export {
  listProjectsCommand,
  getProjectCommand,
  createProjectCommand,
  updateProjectCommand,
  deleteProjectCommand,
} from './commands/projects/index.js';
export {
  listFlowsCommand,
  getFlowCommand,
  createFlowCommand,
  updateFlowCommand,
  deleteFlowCommand,
  duplicateFlowCommand,
} from './commands/flows/index.js';
export {
  deployCommand,
  getDeploymentCommand,
} from './commands/deploy/index.js';
export {
  createDeployCommand,
  listDeploymentsCommand,
  getDeploymentBySlugCommand,
  createDeploymentCommand,
  deleteDeploymentCommand,
} from './commands/deployments/index.js';
export { feedbackCommand } from './commands/feedback/index.js';
export {
  telemetryStatusCommand,
  telemetryEnableCommand,
  telemetryDisableCommand,
} from './commands/telemetry/index.js';

// === Programmatic API ===
// High-level functions for library usage
export { bundle } from './commands/bundle/index.js';
export { buildDataPayload } from './commands/bundle/bundler.js';
export {
  classifyStepProperties,
  containsCodeMarkers,
} from './commands/bundle/config-classifier.js';
export { validateFlowStructure } from './commands/bundle/validate-structure.js';
export { wrapSkeleton } from './commands/bundle/wrap.js';
export type { WrapSkeletonOptions } from './commands/bundle/wrap.js';
export {
  push,
  simulateSource,
  simulateTransformer,
  simulateDestination,
  simulateCollector,
} from './commands/push/index.js';
export type {
  SimulateDataOptions,
  SimulateSourceOptions,
  SimulateTransformerOptions,
  SimulateDestinationOptions,
  SimulateCollectorOptions,
} from './commands/push/index.js';
export { run } from './commands/run/index.js';
export { validate } from './commands/validate/index.js';
export {
  resolveAccessToken,
  getAuthHeaders,
  credentialSource,
  requireProjectId,
} from './core/auth.js';
export { startDeviceAuthorization } from './core/oauth-client.js';
export type { DeviceAuthorization } from './core/oauth-client.js';
export {
  apiFetch,
  publicFetch,
  deployFetch,
  mergeAuthHeaders,
} from './core/http.js';
export { createApiClient } from './core/api-client.js';
export {
  fetchHealth,
  compareContract,
  annotateErrorWithDrift,
  canonicalContractHash,
  bakedContractVersion,
  bakedContractHash,
} from './core/contract.js';
export type {
  HealthResult,
  ContractVerdict,
  ContractComparison,
  CompareContractInput,
} from './core/contract.js';
export { ApiError, throwApiError } from './core/api-error.js';
export type { ApiErrorDetail } from './core/api-error.js';
export {
  setClientContext,
  getClientContext,
  resetClientContext,
  clientContextHeaders,
} from './core/client-context.js';
export type { ClientType, ClientContext } from './core/client-context.js';
export {
  listProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
} from './commands/projects/index.js';
export type { ListProjectsOptions } from './commands/projects/index.js';
export { whoami } from './commands/auth/index.js';
export { feedback } from './commands/feedback/index.js';
export {
  listFlows,
  listAllFlows,
  getFlow,
  createFlow,
  updateFlow,
  deleteFlow,
  duplicateFlow,
} from './commands/flows/index.js';
export { deploy, getDeployment } from './commands/deploy/index.js';
export {
  listDeployments,
  getDeploymentBySlug,
  createDeployment,
  deleteDeployment,
  deleteDeploymentByFlowId,
  DeploymentAmbiguityError,
} from './commands/deployments/index.js';
export type { DeploymentSummaryForFlow } from './commands/deployments/index.js';
export {
  listPreviews,
  getPreview,
  createPreview,
  deletePreview,
  regrantPreview,
} from './commands/previews/index.js';
export type {
  ListPreviewsOptions,
  GetPreviewOptions,
  CreatePreviewOptions,
  DeletePreviewOptions,
  RegrantPreviewOptions,
} from './commands/previews/index.js';
export { listJourneys } from './commands/journeys/index.js';
export type { ListJourneysOptions } from './commands/journeys/index.js';
export {
  startObserveSession,
  getObserveSession,
  endObserveSession,
} from './commands/observe/index.js';
export type {
  StartObserveSessionOptions,
  GetObserveSessionOptions,
  EndObserveSessionOptions,
} from './commands/observe/index.js';
export {
  listReleases,
  getRelease,
  listStepHistory,
  setReleaseRationale,
  listThreads,
  createThread,
  addThreadMessage,
  listKnowledge,
} from './commands/hub/index.js';
export type {
  ListReleasesOptions,
  GetReleaseOptions,
  ReleaseRef,
  ReleaseIndexResponse,
  ReleaseRationaleSummary,
  ReleaseDiffResponse,
  ReleaseDetailResponse,
  ListStepHistoryOptions,
  SetReleaseRationaleOptions,
  ListThreadsOptions,
  CreateThreadOptions,
  AddThreadMessageOptions,
  ListKnowledgeOptions,
  ThreadAnchorType,
  ThreadStatus,
} from './commands/hub/index.js';
export {
  listFrames,
  listPageFrames,
  getFrame,
} from './commands/frames/index.js';
export type {
  ListFramesOptions,
  ListPageFramesOptions,
  GetFrameOptions,
} from './commands/frames/index.js';
export {
  listSecrets,
  createSecret,
  updateSecret,
  deleteSecret,
} from './commands/secrets/index.js';
export type {
  ListSecretsOptions,
  CreateSecretOptions,
  UpdateSecretOptions,
  DeleteSecretOptions,
} from './commands/secrets/index.js';
export type { FeedbackOptions } from './commands/feedback/index.js';
export {
  readConfig,
  writeConfig,
  clearAuthFields,
  deleteConfig,
  resolveToken,
  resolveAppUrl,
  setDefaultProject,
  getDefaultProject,
  setFeedbackPreference,
  getFeedbackPreference,
} from './lib/config-file.js';
export { VERSION } from './version.js';
export type { WalkerOSConfig } from './lib/config-file.js';
export type { ListFlowsOptions, ProjectFlows } from './commands/flows/index.js';
export type { DeployOptions } from './commands/deploy/index.js';
export type { ListDeploymentsOptions } from './commands/deployments/index.js';
export { parseSSEEvents } from './core/sse.js';
export type { SSEEvent, SSEParseResult } from './core/sse.js';

// === Utilities ===
// Export utilities for programmatic usage
export { loadConfig, loadJsonConfig } from './config/utils.js';
export { findExample } from './commands/simulate/example-loader.js';
export { compareOutput } from './commands/simulate/compare.js';

// === Telemetry ===
export * as telemetry from './telemetry/index.js';

// === Types ===
// Export types for programmatic usage
// Config structure uses Flow.Json (root) and Flow (single flow) from @walkeros/core
export type {
  Flow,
  CLIBuildOptions,
  BuildOptions,
  MinifyOptions,
} from './types/bundle.js';
export type { BundleStats } from './commands/bundle/bundler.js';
export type { ExampleLookupResult } from './commands/simulate/example-loader.js';
export type { PushResult } from './commands/push/types.js';
export type { PrepareInput, PreparedFlow } from './commands/push/prepare.js';
export type {
  RunCommandOptions,
  RunOptions,
  RunResult,
} from './commands/run/index.js';
export type { GlobalOptions } from './types/global.js';
export type {
  ValidateResult,
  ValidationType,
  ValidationError,
  ValidationWarning,
} from './commands/validate/types.js';
